import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { WeeklyOffers } from '../components/WeeklyOffers';
import { Header } from '../components/Header';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useServicePalette } from '../hooks/useServicePalette';
import { useRouteId } from '../lib/routeHelper';
import { useCartStore } from '../stores/useCartStore';
import { ShoppingCart } from 'lucide-react';

export function ScheduledOrdersPage() {
    const navigate = useNavigate();
    const routeId = useRouteId();
    const palette = useServicePalette();
    const { scheduledOrders, hasScheduledOrders } = useCartStore();
    const [upcomingOffers, setUpcomingOffers] = useState<any[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [config, setConfig] = useState<any>(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [offersData, productsData, configData] = await Promise.all([
                    apiClient.getUpcomingOffers(),
                    apiClient.getProducts(),
                    apiClient.getConfig()
                ]);
                setUpcomingOffers(offersData as any[]);
                setProducts(productsData as any[]);
                setConfig((configData as any).config);
            } catch (err) {
                console.error('Error loading data:', err);
                setError('No se pudieron cargar los datos.');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Calculate total items across all scheduled dates
    const totalScheduledItems = scheduledOrders.reduce((total, order) => {
        const itemsCount = order.items.reduce((sum, item) => sum + item.quantity, 0);
        const offersCount = order.offers.reduce((sum, item) => sum + item.quantity, 0);
        return total + itemsCount + offersCount;
    }, 0);

    if (loading) {
        return (
            <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900`}>
                <Header title="Cargando..." />
                <LoadingSpinner message="Cargando calendario de ofertas..." />
            </div>
        );
    }

    return (
        <div className={`min-h-screen bg-gradient-to-br ${palette.pageFrom} ${palette.pageVia} ${palette.pageTo} dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-32`}>
            <Header title="Programar Orden" showBack />

            <div className="container mx-auto px-4 py-6 max-w-md">
                {error ? (
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl text-center">
                        {error}
                        <button
                            onClick={() => window.location.reload()}
                            className="block mx-auto mt-2 text-sm font-semibold underline"
                        >
                            Reintentar
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100/50 dark:border-gray-700/50 mb-6">
                            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                                Seleccioná las ofertas que querés para tu pedido programado. Podés agregar ofertas para diferentes días y todas se agregarán al carrito.
                            </p>
                            {hasScheduledOrders() && (
                                <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800">
                                    <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
                                        📅 Tenés {scheduledOrders.length} día{scheduledOrders.length !== 1 ? 's' : ''} con pedidos programados
                                    </p>
                                </div>
                            )}
                        </div>

                        <WeeklyOffers
                            upcomingOffers={upcomingOffers}
                            products={products}
                            config={config}
                        />


                    </>
                )}
            </div>

            {/* Floating Cart Button */}
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-40">
                <button
                    onClick={() => navigate(`/cart/${routeId}`)}
                    disabled={!hasScheduledOrders()}
                    className="w-full btn-primary py-4 rounded-2xl font-bold shadow-lg shadow-indigo-500/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed relative"
                >
                    <ShoppingCart className="w-5 h-5" />
                    Ver Carrito y Confirmar
                    {totalScheduledItems > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                            {totalScheduledItems}
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}
